import React, { useEffect, useRef, useState } from 'react';
import { formatValue } from './formatters';

/**
 * Count-up numerals for the report kit. When a KPI's underlying number
 * changes (filter / cross-filter swap), the displayed value tweens from
 * the old number to the new one instead of snapping — tabular mono
 * numerals make the interpolation read as intentional motion.
 *
 * First render never animates (numbers appear instantly with the page),
 * and prefers-reduced-motion snaps straight to the target.
 */

const DURATION = 700;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Tween a number toward `value` whenever it changes. Non-finite values
 * (null/undefined/NaN) pass through untouched so "—" fallbacks keep
 * working. Returns the in-flight display value.
 */
export function useAnimatedNumber(value, { duration = DURATION } = {}) {
  const target = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  const finite = Number.isFinite(target);
  const [display, setDisplay] = useState(target);
  const ref = useRef({ shown: target, raf: null, mounted: false });

  useEffect(() => {
    const st = ref.current;
    if (st.raf) cancelAnimationFrame(st.raf);
    // first paint, non-numeric targets, or reduced motion: snap
    if (!st.mounted || !finite || !Number.isFinite(st.shown) || prefersReducedMotion()) {
      st.mounted = true;
      st.shown = target;
      setDisplay(target);
      return undefined;
    }
    const from = st.shown;
    if (from === target) return undefined;
    let start;
    const step = (ts) => {
      if (start === undefined) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      const v = t >= 1 ? target : from + (target - from) * easeOutCubic(t);
      st.shown = v;
      setDisplay(v);
      if (t < 1) st.raf = requestAnimationFrame(step);
    };
    st.raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(st.raf);
  }, [target, finite, duration]);

  return finite ? display : value;
}

/**
 * <AnimatedValue value={1234} format="currency" currency="USD" />
 * or with a custom renderer (page-level formatters):
 * <AnimatedValue value={kpi.adr}>{(v) => formatCurrency(v, { decimals: 2 })}</AnimatedValue>
 */
export function AnimatedValue({ value, format = 'number', currency, options, duration, children }) {
  const display = useAnimatedNumber(value, duration ? { duration } : undefined);
  if (typeof children === 'function') return <>{children(display)}</>;
  return <>{formatValue(display, format, currency ? { currency, ...options } : options)}</>;
}

export default AnimatedValue;
